import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { Plus, Ticket, Filter } from 'lucide-react';

const BOOKING_STATUS_COLORS: Record<string, string> = {
  booked: 'bg-yellow-100 text-yellow-800',
  confirmed: 'bg-blue-100 text-blue-800',
  transferred: 'bg-green-100 text-green-800',
  cancelled: 'bg-red-100 text-red-800',
};

const PAYMENT_STATUS_COLORS: Record<string, string> = {
  pending: 'bg-orange-100 text-orange-800',
  partial: 'bg-yellow-100 text-yellow-800',
  paid: 'bg-green-100 text-green-800',
};

export function ProjectBookings() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [addOpen, setAddOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [form, setForm] = useState({
    project_id: '', unit_type_id: '', customer_name: '', customer_phone: '',
    customer_email: '', booking_amount: '', plot_number: '', notes: '',
  });

  // Get developer's projects
  const { data: projects } = useQuery({
    queryKey: ['dev-projects-for-booking', user?.id],
    queryFn: async () => {
      const { data: devProfile } = await supabase.from('developer_profiles').select('id').eq('user_id', user!.id).single();
      if (!devProfile) return [];
      const { data } = await supabase.from('projects').select('id, name, city').eq('developer_id', devProfile.id);
      return data || [];
    },
    enabled: !!user,
  });

  // Get unit types for selected project
  const { data: unitTypes } = useQuery({
    queryKey: ['unit-types', form.project_id],
    queryFn: async () => {
      const { data } = await supabase.from('project_unit_types').select('id, name, bedrooms, price_min').eq('project_id', form.project_id);
      return data || [];
    },
    enabled: !!form.project_id,
  });

  // Get all bookings
  const { data: bookings, isLoading } = useQuery({
    queryKey: ['project-bookings', user?.id],
    queryFn: async () => {
      let qb = supabase
        .from('bookings')
        .select('*, projects(name, city), project_unit_types(name)')
        .eq('developer_user_id', user!.id)
        .order('created_at', { ascending: false });
      const { data, error } = await qb;
      if (error) throw error;
      return (data || []) as any[];
    },
    enabled: !!user,
  });

  const createBooking = useMutation({
    mutationFn: async (data: any) => {
      const { error } = await supabase.from('bookings').insert({
        ...data,
        developer_user_id: user!.id,
        booking_amount: data.booking_amount ? Number(data.booking_amount) : null,
        unit_type_id: data.unit_type_id || null,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project-bookings'] });
      setAddOpen(false);
      setForm({ project_id: '', unit_type_id: '', customer_name: '', customer_phone: '', customer_email: '', booking_amount: '', plot_number: '', notes: '' });
      toast.success('Booking created');
    },
    onError: () => toast.error('Failed to create booking'),
  });

  const updateBookingStatus = useMutation({
    mutationFn: async ({ id, booking_status }: { id: string; booking_status: string }) => {
      const { error } = await supabase.from('bookings').update({ booking_status: booking_status as any }).eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project-bookings'] });
      toast.success('Booking status updated');
    },
  });

  const filtered = (bookings || []).filter((b: any) => statusFilter === 'all' || b.booking_status === statusFilter);

  if (isLoading) {
    return (
      <div className="space-y-4">
        <h2 className="font-display text-xl font-bold">Project Bookings</h2>
        <Skeleton className="h-64 rounded-2xl" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display text-xl font-bold flex items-center gap-2">
            <Ticket className="h-5 w-5 text-primary" /> Project Bookings
          </h2>
          <p className="text-sm text-muted-foreground">Manage unit bookings across your projects.</p>
        </div>
        <Dialog open={addOpen} onOpenChange={setAddOpen}>
          <DialogTrigger asChild>
            <Button size="sm" className="gap-1"><Plus className="h-4 w-4" /> Add Booking</Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>New Booking</DialogTitle></DialogHeader>
            <form onSubmit={e => { e.preventDefault(); createBooking.mutate(form); }} className="space-y-3">
              <div>
                <Label className="text-xs">Project *</Label>
                <Select value={form.project_id} onValueChange={v => setForm(f => ({ ...f, project_id: v, unit_type_id: '' }))}>
                  <SelectTrigger className="h-9 text-sm"><SelectValue placeholder="Select project" /></SelectTrigger>
                  <SelectContent>
                    {(projects || []).map((p: any) => (
                      <SelectItem key={p.id} value={p.id}>
                        {typeof p.name === 'object' ? (p.name as any)?.en : p.name} — {p.city}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {unitTypes && unitTypes.length > 0 && (
                <div>
                  <Label className="text-xs">Unit Type</Label>
                  <Select value={form.unit_type_id} onValueChange={v => setForm(f => ({ ...f, unit_type_id: v }))}>
                    <SelectTrigger className="h-9 text-sm"><SelectValue placeholder="Select unit type" /></SelectTrigger>
                    <SelectContent>
                      {unitTypes.map((u: any) => (
                        <SelectItem key={u.id} value={u.id}>{u.name} {u.bedrooms ? `(${u.bedrooms}BR)` : ''}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <Label className="text-xs">Customer Name *</Label>
                <Input required className="h-9 text-sm" value={form.customer_name} onChange={e => setForm(f => ({ ...f, customer_name: e.target.value }))} />
              </div>
              <div>
                <Label className="text-xs">Customer Phone *</Label>
                <Input required className="h-9 text-sm" value={form.customer_phone} onChange={e => setForm(f => ({ ...f, customer_phone: e.target.value }))} />
              </div>
              <div>
                <Label className="text-xs">Customer Email</Label>
                <Input type="email" className="h-9 text-sm" value={form.customer_email} onChange={e => setForm(f => ({ ...f, customer_email: e.target.value }))} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Booking Amount</Label>
                  <Input type="number" className="h-9 text-sm" value={form.booking_amount} onChange={e => setForm(f => ({ ...f, booking_amount: e.target.value }))} />
                </div>
                <div>
                  <Label className="text-xs">Plot/Unit Number</Label>
                  <Input className="h-9 text-sm" value={form.plot_number} onChange={e => setForm(f => ({ ...f, plot_number: e.target.value }))} />
                </div>
              </div>
              <div>
                <Label className="text-xs">Notes</Label>
                <Textarea rows={2} className="text-sm" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
              </div>
              <Button type="submit" className="w-full" disabled={!form.project_id || !form.customer_name || !form.customer_phone || createBooking.isPending}>
                {createBooking.isPending ? 'Creating...' : 'Create Booking'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filter */}
      <div className="flex items-center gap-2">
        <Filter className="h-4 w-4 text-muted-foreground" />
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[150px] h-8 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="booked">Booked</SelectItem>
            <SelectItem value="confirmed">Confirmed</SelectItem>
            <SelectItem value="transferred">Transferred</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>
        <Badge variant="secondary" className="text-xs">{filtered.length} bookings</Badge>
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground text-sm">
          No bookings found. Create your first booking above.
        </div>
      ) : (
        <div className="rounded-2xl border overflow-hidden bg-card overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Customer</TableHead>
                <TableHead>Project</TableHead>
                <TableHead>Unit</TableHead>
                <TableHead>Plot #</TableHead>
                <TableHead>Amount</TableHead>
                <TableHead>Payment</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Date</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((b: any) => (
                <TableRow key={b.id}>
                  <TableCell>
                    <div className="text-sm font-medium">{b.customer_name}</div>
                    <div className="text-xs text-muted-foreground">{b.customer_phone}</div>
                  </TableCell>
                  <TableCell className="text-sm">
                    {b.projects && (typeof b.projects.name === 'object' ? (b.projects.name as any)?.en : b.projects.name)}
                  </TableCell>
                  <TableCell className="text-sm">{b.project_unit_types?.name || '—'}</TableCell>
                  <TableCell className="text-sm">{b.plot_number || '—'}</TableCell>
                  <TableCell className="text-sm font-medium">
                    {b.booking_amount ? `${Number(b.booking_amount).toLocaleString()} ETB` : '—'}
                  </TableCell>
                  <TableCell>
                    <Badge className={`text-[10px] ${PAYMENT_STATUS_COLORS[b.payment_status] || ''}`}>
                      {b.payment_status}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Select
                      value={b.booking_status}
                      onValueChange={(val) => updateBookingStatus.mutate({ id: b.id, booking_status: val })}
                    >
                      <SelectTrigger className="h-7 w-[120px] text-[10px] border-0 p-0">
                        <Badge className={`text-[10px] ${BOOKING_STATUS_COLORS[b.booking_status] || ''}`}>
                          {b.booking_status}
                        </Badge>
                      </SelectTrigger>
                      <SelectContent>
                        {['booked', 'confirmed', 'transferred', 'cancelled'].map(s => (
                          <SelectItem key={s} value={s} className="capitalize text-xs">{s}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">{new Date(b.booking_date).toLocaleDateString()}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
